<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { Link, router } from '@inertiajs/vue3'

import { AppIcon } from '@/components/shared'
import { search as searchRoute } from '@/routes'

/**
 * Navbar search trigger + autocomplete dropdown.
 *
 * Click on the lupa icon expands a frosted-glass panel containing a debounced
 * input (250 ms) and three result groups - galleries, locations, photographers
 * - three hits each. Backed by `GET /hledat/navrhy?q=...` (JSON endpoint on
 * SearchController::suggest).
 *
 * UX details locked here so /hledat full-page search stays consistent:
 *   - Min query length: 2 chars. Single-character queries blow up the result
 *     set on Postgres trigram + tsvector (every gallery with that letter
 *     matches) so the panel shows a "začni psát" hint until threshold.
 *   - Empty state: distinct copy when query is below threshold vs above-with-
 *     zero-hits ("nic jsme nenašli, zkus jinou frázi").
 *   - Keyboard: ↑ / ↓ move highlight through flattened results, Enter opens
 *     the highlighted hit, Esc closes the panel + restores focus to trigger.
 *   - Click-outside + Escape both close. Trigger focus returns on close so
 *     screen-reader users don't lose their place.
 *   - Mobile (md-): panel goes near-fullscreen (top-strip), still anchored
 *     via Teleport so it escapes navbar's overflow context.
 */
const isOpen = ref(false)
const query = ref('')
const results = ref({ galleries: [], locations: [], photographers: [] })
const isLoading = ref(false)
const highlightIndex = ref(-1)

const rootEl = ref(null)
const inputEl = ref(null)
const triggerEl = ref(null)
const teleportReady = ref(false)

// Flat list of all hits in display order - needed for arrow-key navigation
// that crosses group boundaries seamlessly.
const flatHits = computed(() => [
    ...results.value.galleries,
    ...results.value.locations,
    ...results.value.photographers,
])

const totalHits = computed(() => flatHits.value.length)

const groups = computed(() => [
    { label: 'Galerie', items: results.value.galleries, offset: 0 },
    { label: 'Lokace', items: results.value.locations, offset: results.value.galleries.length },
    {
        label: 'Fotografové',
        items: results.value.photographers,
        offset: results.value.galleries.length + results.value.locations.length,
    },
])

const showEmptyState = computed(() => !isLoading.value && query.value.length >= 2 && totalHits.value === 0)
const showHintState = computed(() => !isLoading.value && query.value.length < 2)

// Debounced fetcher - same 250 ms cadence as the /hledat full page search so
// the perceived rhythm is identical. AbortController kills in-flight requests
// when a new keystroke arrives, preventing late responses from overwriting
// fresher results (race condition on fast typers).
let debounceTimer = null
let abortController = null

function fetchSuggestions(rawQuery) {
    const q = rawQuery.trim()
    if (q.length < 2) {
        results.value = { galleries: [], locations: [], photographers: [] }
        isLoading.value = false
        return
    }

    if (abortController) abortController.abort()
    abortController = new AbortController()
    isLoading.value = true

    fetch(`/hledat/navrhy?q=${encodeURIComponent(q)}`, {
        signal: abortController.signal,
        headers: { Accept: 'application/json' },
    })
        .then((res) => (res.ok ? res.json() : Promise.reject(res)))
        .then((data) => {
            results.value = {
                galleries: Array.isArray(data.galleries) ? data.galleries : [],
                locations: Array.isArray(data.locations) ? data.locations : [],
                photographers: Array.isArray(data.photographers) ? data.photographers : [],
            }
            highlightIndex.value = -1
        })
        .catch((err) => {
            if (err?.name === 'AbortError') return
            // Network/server error → show empty bucket; the "Zobrazit všechny
            // výsledky" footer stays available so the user can still escape
            // into the full /hledat page where errors are surfaced properly.
            results.value = { galleries: [], locations: [], photographers: [] }
        })
        .finally(() => {
            isLoading.value = false
        })
}

watch(query, (next) => {
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => fetchSuggestions(next), 250)
})

function openPanel() {
    isOpen.value = true
    nextTick(() => inputEl.value?.focus())
}

function closePanel({ restoreFocus = true } = {}) {
    const wasOpen = isOpen.value
    isOpen.value = false
    highlightIndex.value = -1

    if (wasOpen && restoreFocus) {
        nextTick(() => triggerEl.value?.focus())
    }
}

function toggleOpen() {
    if (isOpen.value) closePanel()
    else openPanel()
}

function handleClickOutside(event) {
    if (!isOpen.value) return
    if (rootEl.value && !rootEl.value.contains(event.target)) {
        closePanel()
    }
}

function handleEscape(event) {
    if (event.key === 'Escape' && isOpen.value) {
        closePanel()
    }
}

let stopRouterStart = null

onMounted(() => {
    teleportReady.value = true
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    stopRouterStart = router.on('start', () => closePanel({ restoreFocus: false }))
})

onBeforeUnmount(() => {
    document.removeEventListener('mousedown', handleClickOutside)
    document.removeEventListener('keydown', handleEscape)
    stopRouterStart?.()
    clearTimeout(debounceTimer)
    if (abortController) abortController.abort()
})

function moveDown() {
    if (totalHits.value === 0) return
    highlightIndex.value = (highlightIndex.value + 1) % totalHits.value
}

function moveUp() {
    if (totalHits.value === 0) return
    highlightIndex.value = highlightIndex.value <= 0 ? totalHits.value - 1 : highlightIndex.value - 1
}

function selectHighlighted() {
    if (highlightIndex.value < 0 || totalHits.value === 0) {
        // Enter without a selection → fall through to /hledat full page.
        if (query.value.trim().length > 0) {
            router.visit(searchRoute.url({ query: { q: query.value.trim() } }))
        }
        return
    }
    const hit = flatHits.value[highlightIndex.value]
    if (hit?.url) router.visit(hit.url)
}

const showAllUrl = computed(() =>
    query.value.trim().length > 0 ? searchRoute.url({ query: { q: query.value.trim() } }) : searchRoute.url()
)

const iconForType = (type) =>
    ({
        gallery: 'photo_library',
        location: 'location_on',
        photographer: 'person',
    })[type] ?? 'search'
</script>

<template>
    <div
        class="relative"
        ref="rootEl"
    >
        <!--
            Chip-style trigger: subtle border + tinted background gives the icon
            visual mass so it doesn't disappear into the navbar (plain icon was
            essentially invisible at zinc-400 on the dark navbar). Hover lifts
            via orange accent border + brighter icon, mirroring the language
            of the REGISTROVAT CTA without competing with it.
        -->
        <button
            class="text-on-surface hover:text-primary-container hover:border-primary-container/40 relative flex h-11 cursor-pointer items-center gap-2 rounded-full border border-zinc-800 bg-zinc-900/60 px-3 transition-all hover:bg-zinc-900"
            ref="triggerEl"
            type="button"
            :title="isOpen ? 'Zavřít vyhledávání' : 'Hledat na RideSnap'"
            aria-label="Hledat"
            aria-haspopup="dialog"
            :aria-expanded="isOpen"
            @click="toggleOpen"
        >
            <AppIcon
                class="text-xl"
                :name="isOpen ? 'close' : 'search'"
            />
            <span class="teko-font hidden text-base font-bold tracking-widest uppercase lg:inline"> Hledat </span>
        </button>

        <!--
            Page-level scrim - Teleported to body so it escapes the navbar
            stacking context and covers everything beneath the chrome.
            Sits at z-40 (under navbar z-50) so the navbar stays sharp +
            interactive (X button, logo) while the page content blurs out.
            Click anywhere on the scrim closes the panel. aria-hidden because
            it's purely decorative - keyboard users dismiss via Escape.
        -->
        <Teleport
            :disabled="!teleportReady"
            to="body"
        >
            <Transition
                enter-active-class="transition-opacity duration-200 ease-out"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition-opacity duration-150 ease-in"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div
                    class="fixed inset-0 z-40 bg-black/60 backdrop-blur-md"
                    v-if="isOpen"
                    aria-hidden="true"
                    @click="closePanel"
                />
            </Transition>
        </Teleport>

        <Transition
            enter-active-class="transition duration-150 ease-out"
            enter-from-class="-translate-y-2 opacity-0"
            enter-to-class="translate-y-0 opacity-100"
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="translate-y-0 opacity-100"
            leave-to-class="-translate-y-2 opacity-0"
        >
            <!--
                Mobile: fixed viewport-anchored full-width panel (below the
                navbar). Desktop: absolute, anchored to the trigger's
                `rootEl` wrapper and drops down to the right - `md:left-0`
                so the panel grows toward the empty space between the
                discovery cluster and the auth cluster, rather than back
                over the nav links.
            -->
            <div
                class="fixed inset-x-4 top-18 z-55 origin-top-right rounded-lg border border-zinc-800 bg-zinc-950/95 shadow-2xl shadow-black/50 backdrop-blur-2xl md:absolute md:inset-x-auto md:top-full md:left-0 md:mt-2 md:w-md"
                v-if="isOpen"
                role="dialog"
                aria-label="Vyhledávání"
            >
                <!-- Input row - orange focus ring matches the rest of the
                     design system; lupa glyph on the left preserves visual
                     identity with the trigger. -->
                <div class="relative border-b border-zinc-900 p-3">
                    <AppIcon
                        class="absolute top-1/2 left-6 -translate-y-1/2 text-zinc-500"
                        name="search"
                    />
                    <input
                        class="bg-surface-container-low text-on-surface focus:border-primary-container focus:ring-primary-container/40 w-full rounded-md border border-zinc-800 py-3 pr-4 pl-11 text-sm transition-all focus:ring-2 focus:outline-none"
                        v-model="query"
                        ref="inputEl"
                        type="search"
                        placeholder="Brno, Ecce Homo, Honda CBR…"
                        autocomplete="off"
                        aria-label="Vyhledávací dotaz"
                        :aria-activedescendant="highlightIndex >= 0 ? `search-hit-${highlightIndex}` : undefined"
                        @keydown.escape.prevent="closePanel"
                        @keydown.down.prevent="moveDown"
                        @keydown.up.prevent="moveUp"
                        @keydown.enter.prevent="selectHighlighted"
                    />
                </div>

                <!-- Body: hint / loading / empty / results -->
                <div class="max-h-[60vh] overflow-y-auto p-3">
                    <p
                        class="text-on-surface-variant py-6 text-center text-sm"
                        v-if="showHintState"
                    >
                        Začni psát - najdeme galerie, lokace nebo fotografy.
                    </p>

                    <div
                        class="space-y-2 py-2"
                        v-else-if="isLoading"
                        aria-busy="true"
                    >
                        <!-- Skeleton rows - same height as a real hit row. -->
                        <div
                            class="flex items-center gap-3 rounded-md p-2"
                            v-for="n in 3"
                            :key="n"
                        >
                            <div class="h-10 w-10 shrink-0 animate-pulse rounded-md bg-zinc-900" />
                            <div class="flex-1 space-y-2">
                                <div class="h-3 w-2/3 animate-pulse rounded bg-zinc-900" />
                                <div class="h-2 w-1/3 animate-pulse rounded bg-zinc-900/60" />
                            </div>
                        </div>
                    </div>

                    <div
                        class="py-8 text-center"
                        v-else-if="showEmptyState"
                    >
                        <AppIcon
                            class="mb-2 block text-3xl text-zinc-700"
                            name="sentiment_dissatisfied"
                        />
                        <p class="text-on-surface-variant text-sm">
                            Pro „{{ query }}" jsme nic nenašli. Zkus jinou frázi.
                        </p>
                    </div>

                    <div
                        class="space-y-4"
                        v-else
                    >
                        <section
                            v-for="group in groups"
                            v-show="group.items.length > 0"
                            :key="group.label"
                        >
                            <h3
                                class="text-on-surface-variant mb-1 px-2 font-mono text-[10px] font-bold tracking-[0.25em] uppercase"
                            >
                                {{ group.label }}
                            </h3>
                            <ul class="space-y-1">
                                <li
                                    v-for="(hit, i) in group.items"
                                    :key="`${hit.type}-${hit.id}`"
                                >
                                    <Link
                                        class="group flex items-center gap-3 rounded-md p-2 transition-colors"
                                        :id="`search-hit-${group.offset + i}`"
                                        :href="hit.url"
                                        :class="
                                            highlightIndex === group.offset + i
                                                ? 'text-on-surface bg-zinc-900'
                                                : 'text-zinc-300 hover:bg-zinc-900/60'
                                        "
                                        role="option"
                                        :aria-selected="highlightIndex === group.offset + i"
                                        @mouseenter="highlightIndex = group.offset + i"
                                    >
                                        <!-- Image thumbnail when the hit has one;
                                             fall through to the icon-only chip
                                             so empty types still render. -->
                                        <div
                                            class="bg-surface-container-high flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md"
                                            v-if="hit.image_url"
                                        >
                                            <img
                                                class="h-full w-full object-cover"
                                                :src="hit.image_url"
                                                :alt="hit.title"
                                                loading="lazy"
                                            />
                                        </div>
                                        <div
                                            class="bg-surface-container-high text-primary-container flex h-10 w-10 shrink-0 items-center justify-center rounded-md"
                                            v-else
                                            aria-hidden="true"
                                        >
                                            <AppIcon
                                                class="text-lg"
                                                :name="iconForType(hit.type)"
                                            />
                                        </div>
                                        <div class="min-w-0 flex-1">
                                            <p class="truncate text-sm font-semibold">
                                                {{ hit.title }}
                                            </p>
                                            <p
                                                class="text-on-surface-variant truncate text-xs"
                                                v-if="hit.subtitle"
                                            >
                                                {{ hit.subtitle }}
                                            </p>
                                        </div>
                                    </Link>
                                </li>
                            </ul>
                        </section>
                    </div>
                </div>

                <!-- Footer: "show all" link to the full /hledat page. Always
                     visible when there's a query so the user has an escape
                     hatch even when the dropdown is empty / errored. -->
                <div
                    class="border-t border-zinc-900 p-3"
                    v-if="query.trim().length > 0"
                >
                    <Link
                        class="text-primary-container hover:text-primary-container/80 flex items-center justify-center gap-1 rounded-md py-2 font-mono text-[11px] font-bold tracking-widest uppercase transition-colors hover:bg-zinc-900/60"
                        :href="showAllUrl"
                    >
                        Zobrazit všechny výsledky
                        <AppIcon
                            class="text-sm"
                            name="arrow_forward"
                        />
                    </Link>
                </div>
            </div>
        </Transition>
    </div>
</template>
